import React, { useEffect } from "react";
import styles from "./styles/App.module.scss";
import Header from "./components/Header/Header";
import VersionInfoNote from "./components/ChangeLogInfoPopup/VersionInfoNote";
import ChangeLogNotification from "./components/ChangeLogNotification/Notification";
import FaultView from "./containers/FaultView/FaultView";
import Footer from "./components/Footer/Footer";
import { Route, Switch, BrowserRouter } from "react-router-dom";
import Loading from "./components/Loading/Loading";
import api from "./axiosConfig";
import { AuthProvider, logout } from "./components/AuthProvider/AuthProvider";
import { useTranslation } from "react-i18next";
import { getUITexts } from "./i18n";
import { PublicClientApplication } from "@azure/msal-browser";
import { authConfig } from "./components/AuthProvider/authConfig";

export type AppProps = {
  bypassAuthorization: boolean;
};

const publicClientApplication = new PublicClientApplication(authConfig);

// eslint-disable-next-line
const App: React.FC<AppProps> = ({ bypassAuthorization }) => {
  const [error, setError] = React.useState("");
  const [clearItemSelectionTrigger, setClearItemSelectionTrigger] = React.useState(false);
  const [activeUser, setActiveUser] = React.useState(bypassAuthorization ? "test_user" : null);
  const [languageSelectionOpen, setLanguageSelectionOpen] = React.useState(false);
  const [mobileUserMenuOpen, setMobileUserMenuOpen] = React.useState(false);
  const [isFeedbackOpen, setFeedbackOpen] = React.useState(false);

  const { i18n } = useTranslation();

  useEffect(() => {
    api.interceptors.response.use(
      response => {
        return response;
      },
      error => {
        console.log("Error intercepted:", error);

        if (error.response && error.response.status === 401) {
          setError("User Session Expired.Please Click Login to continue or Logout");
          setActiveUser(null);
        }
        return Promise.reject(error);
      }
    );
  }, []);

  /**
   * Checks whetever or not the currently logged in user has the correct role
   * @param user {Api.User} The user from which the roles are checked
   */
  const userHasFWAccess = (user: Api.User): boolean =>
    !!user.roles.find((role: Api.Role) => role.name === process.env.REACT_APP_FAULTVIEWER_ROLE);

  /**
   * Authorizes the user. Modifies the state instead of returning a value.
   */
  const authorizeUser = async (): Promise<void> => {
    try {
      const user: Api.User = await (await api.get("activeUser")).data;
      if (userHasFWAccess(user)) {
        setActiveUser(user.adAccount);
      } else {
        setError(
          `User is not in correct AAD group to access this app. Please contact your admin and request to be added to the faultviewer group`
        );
        setActiveUser(null);
      }
    } catch (error) {
      if (error["response"].status === 403) {
        setError(
          `User is not in correct AAD group to access this app. Please contact your admin and request to be added to the faultviewer group`
        );
      } else if (error["response"].status === 401) {
        setError("User Session Expired.Please Click Login to continue or Logout");
      } else {
        setError("Unexpected error. Please try again in a while");
      }
      setActiveUser(null);
    }
  };

  React.useEffect(() => {
    getUITexts();
    if (!bypassAuthorization) {
      authorizeUser();
    }
  }, [bypassAuthorization]);

  const handleRetryLoginButton = () => {
    logout();
  };

  const handleKoneLogoPressed = () => {
    setClearItemSelectionTrigger(true);
  };

  const getNewToken = () => {
    localStorage.removeItem("token");
    setError("");
    const request = {
      scopes: [process.env.REACT_APP_SCOPES || ""],
      account: publicClientApplication.getAllAccounts()[0]
    };
    publicClientApplication.acquireTokenSilent(request).then(async response => {
      api.defaults.headers.common.Authorization = "Bearer " + response.accessToken;
      const user: Api.User = await (await api.get("activeUser")).data;
      await getUITexts();
      setActiveUser(user.adAccount);
    });
  };

  if (error) {
    return (
      <div className={styles.CenteredDiv}>
        <p>Error: {error}</p>
        <p>Press button to logout and try with another account! or Continue</p>
        <button onClick={handleRetryLoginButton}>Logout</button>&nbsp;&nbsp;
        <button onClick={getNewToken}>Continue</button>
      </div>
    );
  }
  if (!activeUser) {
    return (
      <div className={styles.CenteredDiv}>
        <Loading text="Authorizing user..." />
      </div>
    );
  }
  return (
    <BrowserRouter basename={process.env.PUBLIC_URL}>
      {/*  To send sub pageviews to google analytics we use router's render function for this */}
      <Route
        path="/"
        render={() => {
          return null;
        }}
      />
      <div
        className={styles.App}
        style={{ overflow: mobileUserMenuOpen || languageSelectionOpen ? "hidden" : "auto" }}
      >
        <ChangeLogNotification />
        <VersionInfoNote />
        <Header
          userName={activeUser}
          //userTitle={"Tecnician"}
          setLanguageSelectionOpen={setLanguageSelectionOpen}
          setMobileUserMenuOpen={setMobileUserMenuOpen}
          koneLogoPressed={handleKoneLogoPressed}
          setFeedbackOpen={setFeedbackOpen}
        />
        <div className={styles.ContentDiv}>
          <Switch>
            <Route
              path="/:domain/:faultCode"
              render={props => (
                <FaultView
                  {...props}
                  languageSelectionOpen={languageSelectionOpen}
                  setLanguageSelectionOpen={setLanguageSelectionOpen}
                  language={i18n.language}
                  setLanguage={i18n.changeLanguage}
                  clearItemSelectionTrigger={clearItemSelectionTrigger}
                  setClearItemSelectionTrigger={setClearItemSelectionTrigger}
                  isFeedbackOpen={isFeedbackOpen}
                  setFeedbackOpen={setFeedbackOpen}
                />
              )}
            />
            <Route
              path="/"
              render={props => (
                <FaultView
                  {...props}
                  languageSelectionOpen={languageSelectionOpen}
                  setLanguageSelectionOpen={setLanguageSelectionOpen}
                  language={i18n.language}
                  clearItemSelectionTrigger={clearItemSelectionTrigger}
                  setClearItemSelectionTrigger={setClearItemSelectionTrigger}
                  isFeedbackOpen={isFeedbackOpen}
                  setFeedbackOpen={setFeedbackOpen}
                />
              )}
            />
          </Switch>
        </div>
        <Footer
          userName={activeUser}
          //userTitle={"Tecnician"}
          languageSelectionOpen={languageSelectionOpen}
          setLanguageSelectionOpen={setLanguageSelectionOpen}
          mobileUserMenuOpen={mobileUserMenuOpen}
          setMobileUserMenuOpen={setMobileUserMenuOpen}
          isFeedbackOpen={isFeedbackOpen}
          setFeedbackOpen={setFeedbackOpen}
        />
      </div>
    </BrowserRouter>
  );
};

export default AuthProvider(App);
